import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { Plus, X } from 'lucide-react';
import { Icon } from '@chakra-ui/react';

interface AddMilestoneFormProps {
  rockId: string;
  onAdd: (task: string, dueDate: string, sortOrder: number) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
  existingCount?: number;
}

/**
 * Inline form for adding a new milestone to a rock
 * Uses Formik for form state and Yup for validation
 */
export function AddMilestoneForm({
  rockId,
  onAdd,
  onSuccess,
  onCancel,
  existingCount = 0,
}: AddMilestoneFormProps) {
  const toast = useToast();

  // Color mode values for dark mode support
  const formBg = useColorModeValue('gray.50', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');

  const validationSchema = Yup.object({
    task: Yup.string()
      .required('Task is required')
      .min(3, 'Task must be at least 3 characters'),
    dueDate: Yup.date()
      .required('Due date is required')
      .min(new Date(), 'Due date must be in the future'),
  });

  const handleSubmit = async (
    values: { task: string; dueDate: string },
    { setSubmitting, resetForm }: any
  ) => {
    try {
      await onAdd(values.task, values.dueDate, existingCount);
      toast({
        title: 'Milestone added',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add milestone',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} bg={formBg} borderRadius="md" mb={2}>
      <Formik
        initialValues={{ task: '', dueDate: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <HStack spacing={2} align="flex-end">
              <Field name="task">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.task && form.touched.task} flex={2}>
                    <Input
                      {...field}
                      placeholder="Milestone task"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.task}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="dueDate">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.dueDate && form.touched.dueDate} flex={1}>
                    <Input {...field} type="date" size="sm" bg={inputBg} placeholder="Due date" />
                    <FormErrorMessage fontSize="xs">{form.errors.dueDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Button
                type="submit"
                size="sm"
                colorScheme="blue"
                leftIcon={<Icon as={Plus} w={4} h={4} />}
                isLoading={isSubmitting}
                loadingText="Adding..."
              >
                Add
              </Button>
              {onCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  aria-label="Cancel"
                >
                  <Icon as={X} w={4} h={4} />
                </Button>
              )}
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
